import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { IconRemove } from '@newfront-insurance/core-ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
} from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { Pencil, Search } from 'lucide-react';
import pluralize from 'pluralize';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useSkillListState } from './use-skill-list-state';
import { normalizeSkillCommand } from '../../../../lib/skill-command-utils';
import { CATEGORY_DISPLAY_NAMES, isCustomSkillCommand, type SkillCommand } from '../../../../lib/skill-constants';
import type { CustomSkillRecord } from '../../../../queries/chats';
import { GET_CUSTOM_SKILLS } from '../../../../queries/chats';
import { DELETE_CUSTOM_SKILL } from '../../../../queries/mutations';
import { useChatOverlayContextSafe } from '../../contexts/chat-overlay-context';
import { useSkillsSafe } from '../../contexts/skills-context';
import {
  type NovaAddSkillClickSource,
  getSkillCommandAnalyticsProperties,
  NOVA_ANALYTICS_EVENTS,
  trackNovaEvent,
} from '../../lib/analytics';
import { getErrorMessage } from '../custom-skills-ui';
import {
  SkillCategorySection,
  SkillListChevron,
  SkillListExampleUsage,
  SkillListExpandedHint,
  SkillListExpandedSection,
  SkillListExpandedSubLabel,
  SkillListItem,
} from '../skill-list-item';

export type SkillListVariant = 'default' | 'compact';

interface SkillListProps {
  commands: SkillCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: SkillCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  variant?: SkillListVariant;
  className?: string;
  showKeyboardHints?: boolean;
  onAddSkill?: () => void;
  onEditSkill?: (skill: CustomSkillRecord) => void;
  addSkillClickSource?: NovaAddSkillClickSource;
}

const styles = {
  default: {
    header: 'bg-steel-50 px-3.5 py-2.5',
    headerText: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    footer: 'bg-steel-50 px-3.5 py-2.5',
    footerText: 'text-steel-400 text-sm',
    listMaxHeight: 'max-h-[400px]',
    emptyTitle: 'text-steel-500 text-sm font-semibold',
    emptyDescription: 'text-steel-400 text-sm',
    emptyIcon: 'text-steel-500',
  },
  compact: {
    header: 'bg-steel-50 px-3.5 py-2.5',
    headerText: 'text-steel-400 text-[12px] font-semibold tracking-[1.2px] uppercase',
    footer: 'bg-steel-100 px-3.5 py-2.5',
    footerText: 'text-steel-400 text-[12px]',
    listMaxHeight: '',
    emptyTitle: 'text-steel-500 text-[12px] font-semibold',
    emptyDescription: 'text-steel-400 text-[12px] leading-4',
    emptyIcon: 'text-steel-400',
  },
} as const;

function getGroupCountLabel(commands: SkillCommand[]) {
  return pluralize('skill', commands.length);
}

function InlineIconButton({
  label,
  disabled,
  onClick,
  testId,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-7 w-7 items-center justify-center p-1 text-[#546A83] transition-colors hover:text-[#262D46] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function SkillList({
  commands,
  searchQuery,
  isOpen,
  onSelect,
  onClose,
  inputRef,
  variant = 'default',
  className,
  showKeyboardHints = true,
  onAddSkill,
  onEditSkill,
  addSkillClickSource = 'skill_menu',
}: SkillListProps) {
  const isNovaSkillsManagementEnabled = useFeatureFlag('nova-skills-management', false);
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const notify = useNotify();
  const skillsContext = useSkillsSafe();
  const overlayContext = useChatOverlayContextSafe();
  const hasAccountInContext = Boolean(overlayContext?.accountUuid ?? overlayContext?.selectedAccount?.uuid);
  const isCommandDisabledByAccount = useCallback(
    (command: SkillCommand) => Boolean(command.accountRequired) && !hasAccountInContext,
    [hasAccountInContext],
  );
  const handleSelectCommand = useCallback(
    (command: SkillCommand) => {
      if (isCommandDisabledByAccount(command)) {
        return;
      }
      onSelect(command);
    },
    [isCommandDisabledByAccount, onSelect],
  );
  const {
    filteredCommands,
    groupedCommands,
    selectedIndex,
    expandedCommandUuid,
    listRef,
    toggleExpanded,
    registerItemRef,
  } = useSkillListState({
    commands,
    searchQuery,
    isOpen,
    onSelect: handleSelectCommand,
    onClose,
    inputRef,
  });
  const [skillToDelete, setSkillToDelete] = useState<CustomSkillRecord | null>(null);
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const { data: customSkillsData, refetch: refetchCustomSkills } = useQuery(GET_CUSTOM_SKILLS, {
    variables: { filters: { enabled: [true, false] } },
    skip: !isNovaSkillsManagementEnabled || !isOpen,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [deleteCustomSkill] = useMutation(DELETE_CUSTOM_SKILL);

  const customSkillsByCommand = useMemo(
    () =>
      new Map(
        (customSkillsData?.customSkills ?? []).map((skill) => [normalizeSkillCommand(skill.command), skill] as const),
      ),
    [customSkillsData?.customSkills],
  );

  const refreshSkills = useCallback(async () => {
    await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills({ filters: { enabled: [true, false] } })]);
  }, [refetchCustomSkills, skillsContext]);

  const resolveCustomSkill = useCallback(
    (command: SkillCommand) => {
      if (!isCustomSkillCommand(command)) {
        return null;
      }

      const byId = (customSkillsData?.customSkills ?? []).find((skill) => skill.uuid === command.skill?.id);
      if (byId) {
        return byId;
      }

      return customSkillsByCommand.get(normalizeSkillCommand(command.handle)) ?? null;
    },
    [customSkillsByCommand, customSkillsData?.customSkills],
  );

  const handleDelete = useCallback(async () => {
    if (!skillToDelete) {
      return;
    }

    setDeleteUuid(skillToDelete.uuid);
    try {
      await deleteCustomSkill({
        variables: {
          uuid: skillToDelete.uuid,
        },
      });
      await refreshSkills();
      notify.success('Custom skill deleted.');
      setSkillToDelete(null);
    } catch (deleteError) {
      notify.error(getErrorMessage(deleteError, 'Unable to delete custom skill.'));
    } finally {
      setDeleteUuid(null);
    }
  }, [deleteCustomSkill, notify, refreshSkills, skillToDelete]);

  if (!isOpen) {
    return null;
  }

  const s = styles[variant];
  let itemIndex = 0;

  if (filteredCommands.length === 0) {
    const trimmedQuery = searchQuery.replace(/^\//, '').trim();
    return (
      <div className={cn('flex w-full flex-col overflow-hidden', className)}>
        <div className={s.header}>
          <span className={s.headerText}>Skills</span>
        </div>
        <div className="flex flex-col items-center gap-2.5 bg-white px-3.5 py-4 text-center">
          <Search className={cn('size-6', s.emptyIcon)} />
          <div className="flex flex-col gap-1">
            <p className={s.emptyTitle}>{trimmedQuery ? `No skills match “${trimmedQuery}”` : 'No skills match'}</p>
            <p className={s.emptyDescription}>Try a different keyword</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex w-full flex-col overflow-hidden', className)}>
      <div className={cn(s.header, 'flex items-center justify-between')}>
        <span className={s.headerText}>Skills</span>
        {isNovaSkillsManagementEnabled ? (
          <button
            type="button"
            data-testid="skill-list-add-skill-button"
            onClick={() => {
              trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.ADD_SKILL_CLICKED, {
                chatUuid: chatOverlayContext?.chatUuid,
                source: addSkillClickSource,
              });
              onAddSkill?.();
            }}
            className="text-brand-400 text-sm font-semibold"
          >
            + Add new skill
          </button>
        ) : null}
      </div>

      <div ref={listRef} className={cn('overflow-y-auto', variant === 'default' ? s.listMaxHeight : 'flex-1')}>
        {groupedCommands.map((group) => (
          <SkillCategorySection
            key={group.category}
            title={CATEGORY_DISPLAY_NAMES[group.category] || group.category}
            countLabel={`${group.commands.length} ${getGroupCountLabel(group.commands)}`}
            variant={variant}
          >
            {group.commands.map((command) => {
              const currentIndex = itemIndex;
              const isSelected = currentIndex === selectedIndex;
              const isExpanded = expandedCommandUuid === command.id;
              const customSkill = resolveCustomSkill(command);
              const isDeletePending = customSkill?.uuid === deleteUuid;
              const isDisabledByAccount = isCommandDisabledByAccount(command);
              itemIndex += 1;

              return (
                <SkillListItem
                  key={command.id}
                  primaryActionRef={(el) => registerItemRef(currentIndex, el)}
                  title={variant === 'compact' ? undefined : command.title}
                  handle={`/${command.handle}`}
                  description={command.description}
                  variant={variant}
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  isDisabled={isDeletePending || isDisabledByAccount}
                  disabledTooltip={isDisabledByAccount ? 'Account selection required' : undefined}
                  onPrimaryAction={() => handleSelectCommand(command)}
                  primaryActionAriaLabel={undefined}
                  inlineActions={
                    customSkill && isNovaSkillsManagementEnabled ? (
                      <>
                        <InlineIconButton
                          label={`Delete ${customSkill.frontmatter.name}`}
                          disabled={isDeletePending}
                          onClick={() => setSkillToDelete(customSkill)}
                          testId="skill-list-delete-skill-button"
                        >
                          <IconRemove color="currentColor" size={16} />
                        </InlineIconButton>

                        {onEditSkill ? (
                          <InlineIconButton
                            label={`Edit ${customSkill.frontmatter.name}`}
                            disabled={isDeletePending}
                            onClick={() => onEditSkill(customSkill)}
                            testId="skill-list-edit-skill-button"
                          >
                            <Pencil className="h-4 w-4" />
                          </InlineIconButton>
                        ) : null}
                      </>
                    ) : undefined
                  }
                  trailingAction={
                    <SkillListChevron
                      isExpanded={isExpanded}
                      onClick={() => {
                        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_COMMAND_EXPANDED, {
                          ...getSkillCommandAnalyticsProperties(command),
                          chatUuid: chatOverlayContext?.chatUuid,
                          expanded: !isExpanded,
                        });
                        toggleExpanded(command.id, currentIndex);
                      }}
                      label={isExpanded ? `Collapse ${command.handle}` : `Expand ${command.handle}`}
                      disabled={isDeletePending}
                      variant={variant}
                    />
                  }
                  expandedContent={
                    <>
                      <SkillListExpandedSection label="What it does" variant={variant}>
                        <div className="flex min-w-0 flex-col gap-1">
                          <SkillListExpandedSubLabel variant={variant}>
                            Use `/{command.handle}` to load this skill
                          </SkillListExpandedSubLabel>
                          <div className="mt-2">
                            <SkillListExpandedHint variant={variant}>{command.overview}</SkillListExpandedHint>
                          </div>
                        </div>
                      </SkillListExpandedSection>

                      {command.exampleUsage ? (
                        <SkillListExpandedSection label="Example usage" variant={variant}>
                          <SkillListExampleUsage handle={command.handle} variant={variant}>
                            {command.exampleUsage}
                          </SkillListExampleUsage>
                        </SkillListExpandedSection>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </SkillCategorySection>
        ))}
      </div>

      {showKeyboardHints ? (
        <div className={s.footer}>
          <span className={s.footerText}>
            <kbd className="rounded px-1 py-0.5">↑↓</kbd> to navigate{' '}
            <kbd className="ml-2 rounded px-1 py-0.5">Tab</kbd>/<kbd className="rounded px-1 py-0.5">Enter</kbd> to
            select <kbd className="ml-2 rounded px-1 py-0.5">Esc</kbd> to cancel
          </span>
        </div>
      ) : null}

      <AlertDialog open={Boolean(skillToDelete)} onOpenChange={(open) => !open && setSkillToDelete(null)}>
        <AlertDialogContent className="w-[349px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete custom skill</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {skillToDelete ? (
              <>
                This removes <span className="font-mono font-semibold text-[#257FFB]">/{skillToDelete.command}</span>{' '}
                from your custom skills list.
              </>
            ) : (
              ''
            )}
          </AlertDialogDescription>
          <AlertDialogFooter className="flex-row justify-end">
            <AlertDialogCancel disabled={Boolean(deleteUuid)}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="dangerous" disabled={Boolean(deleteUuid)} onClick={() => void handleDelete()}>
              {deleteUuid ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
